import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNewAuth } from '../../contexts/NewAuthContext';
import '../../css/unifiedLogin.css';
import VersionInfo from '../../components/VersionInfo';
import { getWelcomeMessage } from '../../utils/userUtils';

const NewApiLogin = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [focus, setFocus] = useState('');
  const [tried, setTried] = useState(false);
  // Solo usar el nuevo API de Laravel
  const { login, isAuthenticated, isLoading, user } = useNewAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirigir si ya está autenticado (solo si no estamos en proceso de login)
  useEffect(() => {
    if (isAuthenticated && !isLoading && !isSubmitting) {
      const from = location.state?.from?.pathname || '/home';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, isSubmitting, navigate, location]);

  // No manejar errores del contexto automáticamente para evitar conflictos

  // Limpiar estado al montar el componente
  useEffect(() => {
    setMessage({ type: '', text: '' });
  }, []);

  // Validaciones en tiempo real
  const validateField = (name, value) => {
    const newErrors = { ...errors };
    
    switch (name) {
      case 'email':
        if (!value) {
          newErrors.email = 'El correo electrónico es requerido';
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          newErrors.email = 'El formato del correo no es válido';
        } else {
          delete newErrors.email;
        }
        break;
      
      case 'password':
        if (!value) {
          newErrors.password = 'La contraseña es requerida';
        } else if (value.length < 3) {
          newErrors.password = 'La contraseña debe tener al menos 3 caracteres';
        } else {
          delete newErrors.password;
        }
        break;
      
      default:
        break;
    }
    
    setErrors(newErrors);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
    
    setTried(false);
    // Limpiar todos los mensajes al cambiar campos
    setMessage({ type: '', text: '' });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'El correo electrónico es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El formato del correo no es válido';
    }
    
    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 3) {
      newErrors.password = 'La contraseña debe tener al menos 3 caracteres';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setTried(true);
    
    // Limpiar errores previos
    setMessage({ type: '', text: '' });
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);

    try {
      setMessage({ 
        type: 'info', 
        text: 'Iniciando sesión...' 
      });

      // Usar solo el contexto para el login
      const result = await login(formData.email, formData.password);
      
      console.log('Login result:', result);
      console.log('isAuthenticated after login:', isAuthenticated);
      
      if (result?.success) {
        // Esperar un momento para que el contexto se actualice con los datos del usuario
        setTimeout(() => {
          const welcomeMsg = getWelcomeMessage(user);
          setMessage({ 
            type: 'success', 
            text: `${welcomeMsg} Redirigiendo...` 
          });
        }, 100);
        
        // Navegación inmediata después del login exitoso
        const targetPath = location.state?.from?.pathname || '/home';
        console.log('Navigating to:', targetPath);
        
        // Forzar navegación usando window.location sin esperar
        window.location.href = `#${targetPath}`;
        
        // También actualizar la URL usando React Router como fallback
        navigate(targetPath, { replace: true });
        
      } else {
        console.log('Login falló - mostrando mensaje de error:', result?.message);
        setMessage({ 
          type: 'error', 
          text: result?.message || 'Credenciales incorrectas. Verifica tu email y contraseña.' 
        });
      }
    } catch (error) {
      console.error('Error en login:', error);
      setMessage({ 
        type: 'error', 
        text: 'Error de conexión. Verifica tu conexión a internet e intenta nuevamente.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const lockIcon = (stroke) => (
    <svg className="lk-ico" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="5" y="11" width="14" height="9" rx="2.5" /><path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );

  // Estado visual del candado
  const hasErrors = Object.keys(errors).length > 0;
  const bad = message.type === 'error' || (tried && hasErrors);
  const state = message.type === 'success' ? 'ok' : bad ? 'err' : 'idle';
  const open = (focus === 'pass' || showPassword) && !isSubmitting && state !== 'ok';
  const lockClass = ['lk-lock', 'lk-glow', state === 'ok' && 'lk-ok', state === 'err' && 'lk-err', open && 'lk-open', isSubmitting && 'lk-busy']
    .filter(Boolean).join(' ');
  const lockLabel = state === 'ok' ? 'Candado cerrado, acceso concedido' : open ? 'Candado abierto' : 'Candado cerrado';

  if (isLoading) {
    return (
      <div className="lk-page">
        <div className="lk-orb lk-orb1" />
        <div className="lk-orb lk-orb2" />
        <div className="lk-card" role="status" aria-live="polite">
          <h1 className="lk-title">Cargando...</h1>
          <div className="lk-msg" style={{ justifyContent: 'center' }}><div className="lk-spin" /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="lk-page">
      <div className="lk-orb lk-orb1" />
      <div className="lk-orb lk-orb2" />
      <div className="lk-dots" />

      <div className={`lk-card${bad ? ' lk-bad' : ''}`}>
        <div className={lockClass}>
          <svg width="190" height="190" viewBox="0 0 200 200" role="img" aria-label={lockLabel}>
            <defs>
              <linearGradient id="lk-body" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#8b9bff" /><stop offset="1" stopColor="#764ba2" /></linearGradient>
              <linearGradient id="lk-shk" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#e8ebff" /><stop offset="1" stopColor="#9aa6e8" /></linearGradient>
              <linearGradient id="lk-okg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#5eead4" /><stop offset="1" stopColor="#22a06b" /></linearGradient>
              <radialGradient id="lk-halo">
                <stop className="lk-g-stop" offset="0" stopOpacity=".55" />
                <stop className="lk-g-stop" offset="1" stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle cx="100" cy="100" r="96" fill="url(#lk-halo)" />
            <circle className="lk-pulse lk-g-stroke" cx="100" cy="100" r="62" fill="none" strokeWidth="2" />
            <circle className="lk-pulse lk-p2 lk-g-stroke" cx="100" cy="100" r="62" fill="none" strokeWidth="2" />
            <g className="lk-ringA"><circle className="lk-g-stroke" cx="100" cy="100" r="86" fill="none" strokeOpacity=".55" strokeWidth="1.5" strokeDasharray="2 9" strokeLinecap="round" /></g>
            <g className="lk-ringB">
              <circle cx="100" cy="100" r="74" fill="none" stroke="#fff" strokeOpacity=".28" strokeWidth="1.5" strokeDasharray="46 22 8 22" />
              <circle className="lk-g-fill" cx="100" cy="26" r="4" />
            </g>
            <g className="lk-shackle">
              <path d="M76 98 V82 a24 24 0 0 1 48 0 V98" fill="none" stroke="url(#lk-shk)" strokeWidth="12" strokeLinecap="round" />
            </g>
            <rect x="62" y="96" width="76" height="62" rx="16" fill={state === 'ok' ? 'url(#lk-okg)' : 'url(#lk-body)'} />
            <rect x="62" y="96" width="76" height="30" rx="16" fill="#fff" opacity=".14" />
            <g className="lk-keyhole">
              <circle cx="100" cy="123" r="7" fill="#1a1445" />
              <rect x="97" y="126" width="6" height="14" rx="3" fill="#1a1445" />
            </g>
            {state === 'ok' && (
              <path className="lk-tick" d="M86 126 l10 10 l19 -22" fill="none" stroke="#fff" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
            )}
          </svg>
        </div>

        <h1 className="lk-title">Bienvenido a <span className="lk-brand">ALTRISA</span></h1>
        <p className="lk-sub">Sistema Financiero Inteligente</p>

        <form className="lk-form" onSubmit={handleSubmit} noValidate>
          <div>
            <div className="lk-field">
              <svg className="lk-ico" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8e96c4" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="3" /><path d="m4 8 8 6 8-6" />
              </svg>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                onFocus={() => setFocus('email')}
                className={`lk-input${errors.email ? ' lk-invalid' : ''}`}
                placeholder="Correo electrónico"
                aria-label="Correo electrónico"
                aria-invalid={!!errors.email}
                disabled={isSubmitting}
                autoComplete="email"
              />
            </div>
            {errors.email && <span className="lk-ferr">{errors.email}</span>}
          </div>

          <div>
            <div className="lk-field">
              {lockIcon('#8e96c4')}
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                onFocus={() => setFocus('pass')}
                className={`lk-input lk-pass${errors.password ? ' lk-invalid' : ''}`}
                placeholder="Contraseña"
                aria-label="Contraseña"
                aria-invalid={!!errors.password}
                disabled={isSubmitting}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="lk-eye"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isSubmitting}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" />
                  {!showPassword && <path d="M4 4l16 16" />}
                </svg>
              </button>
            </div>
            {errors.password && <span className="lk-ferr">{errors.password}</span>}
          </div>

          <div className={`lk-msg ${message.type}`} role="status" aria-live="polite">
            {message.type === 'info' && <div className="lk-spin" />}
            {message.text}
          </div>

          <button type="submit" className="lk-btn" disabled={isSubmitting || hasErrors}>
            {isSubmitting && <div className="lk-spin" />}
            {isSubmitting ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>
        </form>

        <div className="lk-foot">
          <span className="lk-led" />
          Conexión cifrada · ALTRISA © 2026
        </div>
      </div>

      <div className="lk-version"><VersionInfo variant="footer" /></div>
    </div>
  );
};

export default NewApiLogin;
